<template>
  <Transition mode="out-in">
    <div v-if="!hasData && !skeleton" key="empty" class="flex flex-col gap-2">
      <div
        class="mt-0.5! -mb-px! flex flex-col gap-2 self-center pt-14! pb-14! lg:mt-0! lg:mb-0! lg:pt-0! lg:pb-0!"
      >
        <RfNoChartDataPlaceholder
          v-if="!alterNoData"
          class="pt-10"
          body="There is no data yet. Data is displayed when a prompt(s) has been running for at least two hours."
          style="height: 364px"
        />
        <RfNoChartDataPlaceholderAlter v-else class="mb-1 pt-10" style="height: 364px" />
      </div>
      <div class="h-5 pt-2"></div>
    </div>
    <div v-else key="chart" class="flex flex-col gap-3">
      <div
        v-if="skeleton"
        class="mt-px mb-0.5 grid grid-cols-3 gap-2 gap-y-6 border-none px-4 py-3 lg:grid-cols-6"
      >
        <div v-for="i in 6" :key="i" class="flex flex-col gap-3">
          <RfBaseSkeleton height="16" class="w-5/6!" width="146" />
          <RfBaseSkeleton height="25" class="w-2/3!" width="106" />
          <!-- <div class="flex items-center gap-1.5">
            <RfBaseSkeleton height="24" width="24" radius="9999" />
            <RfBaseSkeleton height="16" class="w-1/6!" width="29" />
          </div> -->
        </div>
      </div>
      <div
        v-else
        ref="statsButtonsContainer"
        class="border-strokes grid w-full max-w-full min-w-px auto-cols-fr grid-flow-col grid-rows-2 border-y border-solid px-0 py-0 lg:grid-rows-1"
      >
        <button
          v-for="({ totals, label }, index) in data"
          :key="totals.label || label"
          class="grid place-items-start border-b-2 border-solid border-transparent px-4 py-2"
          :disabled="+totals.value === 0"
          :class="{
            'bg-beige-1 border-beige-90!': selectedChartKey === index + 1,
            'hover:bg-beige-1': +totals.value > 0,
            'cursor-default!': +totals.value === 0,
          }"
          @click="selectChartKey(index + 1)"
        >
          <RfStatsCard
            :title="totals.label || label"
            class="min-w-px flex-1 border-none! bg-transparent! p-0! text-start shadow-none! [&_.rf-stats-card--title]:truncate"
            :value="totals.text || totals.value"
            :nativeTitle="`${totals.label || label}: ${totals.text || totals.value}`"
            :grow="0"
            :timePeriod="''"
          />
        </button>
      </div>
      <RfChart
        :noLegend="noLegend"
        :data="data"
        :skeleton="skeleton"
        :hasData="hasData"
        :selectedChartKey="selectedChartKey"
      >
        <slot></slot>
      </RfChart>
    </div>
  </Transition>
</template>

<script setup>
import { newChartHasData } from "@/utils/metricsHelpers";
import RfStatsCard from "@/components/cards/RfStatsCard.vue";
import RfNoChartDataPlaceholderAlter from "@/components/placeholder/RfNoChartDataPlaceholderAlter.vue";
import RfBaseSkeleton from "@/components/skeletons/RfBaseSkeleton.vue";
import { computed, onUnmounted, ref, watchEffect } from "vue";
import { onClickOutside } from "@vueuse/core";
import RfNoChartDataPlaceholder from "@/components/placeholder/RfNoChartDataPlaceholder.vue";
import RfChart from "./RfChart.vue";

const props = defineProps({
  skeleton: { type: Boolean, default: false },
  alterNoData: { type: Boolean, default: false },
  data: { type: Array, default: () => [] },
  noLegend: { type: Boolean, default: false },
  hasData: { type: [Boolean, undefined], default: undefined },
});

const stop = ref(() => {});
const statsButtonsContainer = ref(null);
const selectedChartKey = ref(null);
const hasData = computed(() =>
  typeof props.hasData === "boolean" ? props.hasData : newChartHasData(props.data),
);

const selectChartKey = key => {
  if (key === selectedChartKey.value) return (selectedChartKey.value = null);
  return (selectedChartKey.value = key);
};

watchEffect(() => {
  if (!props.skeleton && hasData.value) {
    stop.value = onClickOutside(statsButtonsContainer.value, () => (selectedChartKey.value = null));
  } else {
    stop.value?.();
  }
});

onUnmounted(stop?.value);

defineExpose({ hasData, selectedChartKey });
</script>

<style lang="scss" scoped>
.flex,
.child-flex > * {
  flex: 1 1 auto;
  max-width: 100%;
}
</style>
